/**
 * Fee and places, now that both live on the ACTIVITY rather than on the event's
 * registration row.
 *
 * An event can carry several activities at different prices, and the rail has
 * room for one number. So the card answers the question a visitor actually
 * asks — "what does it cost to enter this event?" — with the cheapest way in,
 * and says "From" when they are not all the same. Printing one activity's price
 * as though it were the event's would be wrong for every other activity.
 *
 * Every function here tolerates missing data, because the public data source
 * does not expose these columns yet: no fees at all reads as free, and no
 * places at all reads as unknown rather than as zero. Zero places would tell a
 * visitor the event is full.
 */

const isNum = value => value !== '' && value !== null && value !== undefined
  && Number.isFinite(Number(value));

function feesOf(activities) {
  return (Array.isArray(activities) ? activities : [])
    .map(activity => activity?.fee_amount)
    .filter(isNum)
    .map(Number);
}

/**
 * What the event costs to enter.
 *
 *   { isPaid, amount, isFrom }
 *
 * `isFrom` says the amount is the cheapest of several, so the caller can word
 * it as "From ₹500" rather than stating it flatly.
 */
export function summariseFee(activities, { fallbackIsPaid = false } = {}) {
  const fees = feesOf(activities);

  // Nothing to go on — fall back to the event's own flag, which is what the
  // page used before fees moved, so an event with no activity data still shows
  // the right kind of entry.
  if (fees.length === 0) return { isPaid: Boolean(fallbackIsPaid), amount: null, isFrom: false };

  const paid = fees.filter(fee => fee > 0);
  if (paid.length === 0) return { isPaid: false, amount: 0, isFrom: false };

  const cheapest = Math.min(...paid);
  // "From" only when the prices genuinely differ — including a free activity
  // alongside paid ones, where the cheapest way in is not the one quoted.
  const isFrom = paid.length < fees.length || paid.some(fee => fee !== cheapest);

  return { isPaid: true, amount: cheapest, isFrom };
}

/**
 * Does a price shown outside the checkout need a "+ GST" beside it?
 *
 * WHY THE RAIL SAYS IT AT ALL. The CCPA's Guidelines for Prevention and
 * Regulation of Dark Patterns, 2023 name "drip pricing" — holding back price
 * components that apply to all or most buyers until late in the flow — as an
 * unfair trade practice. GST on a ticket over the threshold applies to every
 * buyer without exception, so printing a bare fee and charging 18% more at the
 * gateway is squarely the pattern described. The suffix costs a visitor
 * nothing and takes the listing out of that frame.
 *
 * THE THRESHOLD IS AN ARGUMENT, NOT A CONSTANT. It lives on the server's tax
 * rule, where it can be edited without a deploy; a figure hard-coded here
 * would go on claiming the old threshold after the rule behind the charge had
 * moved, and the rail would then disagree with the checkout. Null threshold —
 * which is what a caller with no server figure in hand passes — reads as "do
 * not know", and nothing is appended. Silence is the only safe default: a
 * missing suffix understates a price, while a wrong one misstates it.
 *
 * Exclusive comparison, matching the rule's own column: a fee EQUAL to the
 * threshold is exempt, and only one above it is taxed.
 */
export function needsTaxSuffix(fee, exemptUpto) {
  if (exemptUpto === null || exemptUpto === undefined) return false;
  if (!isNum(fee) || !isNum(exemptUpto)) return false;
  return Number(fee) > Number(exemptUpto);
}

/**
 * How many people can enter, across the whole event.
 *
 * Summed, because the activities run in parallel and each holds its own field.
 * Null when no activity declares a limit — an event with no cap is not an event
 * with no room.
 */
export function totalPlaces(activities) {
  const caps = (Array.isArray(activities) ? activities : [])
    .map(activity => activity?.max_participants)
    .filter(isNum)
    .map(Number);

  if (caps.length === 0) return null;
  return caps.reduce((sum, cap) => sum + cap, 0);
}
